import { Drawer, Transition } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Cookies from 'js-cookie';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogin } from '@/hooks/useLogin';
import Navigation from '@/layouts/Navigation';
import { useGPTStore } from '@/store';
import { AvatarMenu, AvatarMenuItem, deleteTokenInfo, KEY_MYGPT_TOKEN_EXPIRESIN, useTracking } from '@mygpt/app';
import { IconLogout, IconSettings, IconUser } from '@tabler/icons-react';

import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';

const Header = () => {
  const { t } = useTranslation('', { lng: 'ja' });
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  // const { login, scheduleTokenRenewal, signup } = useLogin();
  const { logout, scheduleTokenRenewal } = useLogin();
  const { fetchUserInfo, userInfo } = useGPTStore((state) => state);
  const { trackSignupClick, trackLoginClick, trackContactClick } = useTracking();

  const [opened, { close, toggle }] = useDisclosure(false);
  const [isScrollTop, setIsScrollTop] = useState(true);
  const [avatarReady, setAvatarReady] = useState(false);

  // 检查登录状态，获取用户信息
  useLayoutEffect(() => {
    const tokenExpires = Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN());
    if (!tokenExpires) return;

    const nowTime = new Date().getTime();
    const expiresTime = Number(tokenExpires);

    if (expiresTime > nowTime) {
      fetchUserInfo().then(() => {
        setAvatarReady(true);
      });
    } else {
      deleteTokenInfo();
    }
  }, [fetchUserInfo]);

  // 延迟启动 token 续期
  useEffect(() => {
    const timer = setTimeout(() => {
      scheduleTokenRenewal();
    }, 10 * 1000);
    return () => clearTimeout(timer);
  }, []);

  // 用户信息加载后的过渡动画
  useEffect(() => {
    if (userInfo) {
      setAvatarReady(false);
      const timer = setTimeout(() => setAvatarReady(true), 250);
      return () => clearTimeout(timer);
    }
  }, [userInfo]);

  // 路由变化时关闭移动菜单
  useLayoutEffect(() => {
    close();
  }, [pathname, close]);

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      setIsScrollTop(window.pageYOffset === 0);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 构建头像菜单项
  const menuItems: AvatarMenuItem[] = useMemo(
    () => [
      {
        key: 'account',
        label: t('account.title', '個人センター'),
        onClick: () => navigate('/account'),
        icon: <IconUser size={15} />,
      },
      {
        key: 'admin',
        label: t('navigation.home', '管理画面'),
        onClick: () => {
          window.location.href = import.meta.env.VITE_ADMIN_URL;
        },
        icon: <IconSettings size={15} />,
      },
      {
        key: 'logout',
        label: t('logout', 'ログアウト'),
        onClick: () => logout(),
        color: 'danger' as const,
        icon: <IconLogout size={15} />,
      },
    ],
    [t, navigate, logout]
  );

  return (
    <>
      {/* Desktop & Mobile Header */}
      <div
        className={`sticky top-0 z-50 w-full border-b border-[#ECEAF5] bg-white/95 backdrop-blur-sm transition-shadow ${
          !isScrollTop ? 'shadow-sm' : ''
        }`}
      >
        <div className="px-4 md:px-10 lg:px-40 flex justify-center">
          <div className="flex h-16 w-full max-w-[1200px] items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <SmartLink to="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img
                  src="https://cs.gbase.ai/title-logo.png"
                  alt="GBase Support - 商業施設向けAIチャットボット・AIコンシェルジュ"
                  className="h-8 w-auto"
                />
              </SmartLink>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center">
              <Navigation />
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <SmartLink
                href={import.meta.env.VITE_ADMIN_URL}
                className="text-sm font-medium text-[#555573] hover:text-violet-500 transition-colors"
                onClick={() => trackLoginClick('header')}
              >
                ログイン
              </SmartLink>
              <SmartLink
                to="/register"
                className="flex items-center justify-center rounded-full h-10 px-5 border-[1.5px] border-[#D7D2EA] hover:border-violet-500 hover:bg-violet-500/10 hover:shadow-[0_0_0_3px_rgba(139,92,246,0.15)] transition-all text-[#1A1A2E] text-sm font-medium"
                onClick={() => trackSignupClick('header')}
              >
                無料ではじめる
              </SmartLink>
              <SmartLink
                href="/contact"
                className="flex items-center justify-center rounded-full h-10 px-5 bg-gradient-to-br from-violet-500 to-indigo-500 hover:-translate-y-px transition-all text-white text-sm font-medium shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:shadow-[0_8px_24px_rgba(139,92,246,0.4)]"
                onClick={() => trackContactClick('header')}
              >
                お問い合わせ
              </SmartLink>
              <Transition
                mounted={!!userInfo && avatarReady}
                transition="slide-up"
                duration={200}
                timingFunction="ease"
              >
                {(styles) => (
                  <section style={styles}>
                    {userInfo && <AvatarMenu userInfo={userInfo} menuItems={menuItems} lang="ja" />}
                  </section>
                )}
              </Transition>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 hover:bg-gray-100 rounded-md transition-colors" onClick={toggle}>
              <MaterialIcon name="menu" className="text-2xl text-[#1A1A2E]" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        opened={opened}
        onClose={close}
        size="sm"
        position="right"
        withCloseButton={false}
        classNames={{ body: 'p-0' }}
      >
        <div className="px-4 py-4 flex flex-col gap-4">
          <Navigation stack onClose={close} />

          <hr className="border-[#ECEAF5] my-2" />

          {/* Mobile CTA Buttons */}
          <SmartLink
            href={import.meta.env.VITE_ADMIN_URL}
            className="text-sm font-medium text-[#1A1A2E]"
            onClick={() => trackLoginClick('mobile_menu')}
          >
            ログイン
          </SmartLink>
          <SmartLink
            to="/register"
            className="flex items-center justify-center rounded-full h-10 px-5 border border-[#ECEAF5] text-[#1A1A2E] text-sm font-medium"
            onClick={() => trackSignupClick('mobile_menu')}
          >
            無料ではじめる
          </SmartLink>
          <SmartLink
            href="/contact"
            className="flex items-center justify-center rounded-full h-10 px-5 bg-gradient-to-r from-violet-500 to-indigo-600 text-white text-sm font-medium"
            onClick={() => trackContactClick('mobile_menu')}
          >
            お問い合わせ
          </SmartLink>
          {userInfo && (
            <div className="absolute bottom-5 left-5 w-full px-5">
              <AvatarMenu userInfo={userInfo} menuItems={menuItems} lang="ja" />
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default Header;
